import { FC } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Loading } from '@components/atoms/Loading';
import { useTranslation } from '@hooks/useTranslation';
import { useDailySiteReport } from '@hooks/api/dailySiteReport';
import { TriggerChip } from '@components/organisms/DailySiteReport/TriggerChip';
import { EmailStatusChip } from '@components/organisms/DailySiteReport/EmailStatusChip';
import { ReportView } from '@components/organisms/DailySiteReport/ReportView';
import { DailySiteReportEmailStatus } from '@models/dailySiteReport.model';

const DailySiteReportDetailPage: FC = () => {
  const t = useTranslation('dailySiteReport');
  const history = useHistory();
  const { reportId } = useParams<{ reportId: string }>();
  const { data: report, isLoading, isError } = useDailySiteReport(reportId);

  return (
    <div>
      <button
        type="button"
        onClick={() => history.push('/reports/daily')}
        className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 mb-4">
        <ArrowBackIcon fontSize="small" />
        {t.backToList}
      </button>

      {isLoading && <Loading center size="lg" />}

      {!isLoading && (isError || !report) && (
        <div className="text-center py-16">
          <p className="text-lg font-medium text-gray-900 dark:text-white">{t.notFoundTitle}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{t.notFoundBody}</p>
        </div>
      )}

      {!isLoading && report && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {new Date(`${report.localDate}T12:00:00`).toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </h1>
            <TriggerChip trigger={report.trigger} />
            <EmailStatusChip status={report.emailStatus} />
          </div>

          {report.emailStatus === DailySiteReportEmailStatus.FAILED && report.errorMessage && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-200">
              {t.errorRowNotice.replace('{message}', report.errorMessage)}
            </div>
          )}

          <ReportView sections={report.sections} />
        </div>
      )}
    </div>
  );
};

export default DailySiteReportDetailPage;
